import React from 'react';
import { CircularProgress, Tooltip, Typography } from '@material-ui/core';
import EventNoteIcon from '@material-ui/icons/EventNote';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { activeSchedule, scheduleRuntimeInfo, schedulingSettingsReady, supportsScheduling, supportsScheduleProtocol } from '../../Utils/scheduling_utils';
import { isZoned } from '../../Utils/utils';
import { useScheduleSubmissionTimeout } from './useScheduleDraft';
import { scheduleSubmissionMessage } from '../../Utils/scheduling_transaction';
import useSchedulingEnabled from '../../Hooks/useSchedulingEnabled';

/** Header information uses controller readback, independently of recipe drafts. */
export default function ScheduleStatus({ controller, parent, className }) {
  const schedulingEnabled = useSchedulingEnabled();
  const { t } = useTranslation();
  const snapshot = useSnapshot(parent);
  const liveController = useSnapshot(controller);
  const { scheduling } = snapshot.settings;
  const submission = snapshot.schedule_submission;
  useScheduleSubmissionTimeout(parent, submission);
  const active = activeSchedule(liveController, parent, scheduling);
  const runtime = scheduleRuntimeInfo(scheduling);
  const failure = ['conflict', 'rejected', 'unconfirmed', 'unsent'].includes(submission?.status);
  // Committed settings may precede their runtime packet. ESP initially marks
  // that recipe invalid with disabled runtime until SAM supplies its state.
  const awaitingRuntime = scheduling?.schedule_meta?.schedule_enabled &&
    (scheduling?.schedule_values?.active_recipe_revision !== scheduling?.schedule_values?.recipe_revision ||
      (!scheduling?.schedule_values?.recipe_valid && scheduling?.schedule_values?.runtime_status === 0));
  const runtimeProblem = ['schedule_runtime_invalid', 'schedule_runtime_unknown'].includes(runtime?.status);
  const busy = !failure && (submission?.status === 'pending' || !schedulingSettingsReady(scheduling) ||
    (!runtimeProblem && awaitingRuntime));
  if (!schedulingEnabled || (isZoned(liveController) && parent === controller) ||
      !liveController.access_rights?.scheduling?.read ||
      !supportsScheduling(liveController) ||
      (scheduling?.schedule_meta && !supportsScheduleProtocol(liveController, scheduling)) ||
      (!active && !busy && !failure && !runtimeProblem)) return null;
  const label = active ? `${active.phaseNumber}${active.phaseName ? ` · ${active.phaseName}` : ''}` : '';
  const description = failure ? t(scheduleSubmissionMessage(submission)) :
    busy ? t(submission?.status === 'pending' ? 'schedule_submission_pending' : 'schedule_loading') :
      runtimeProblem ? t(runtime.status) :
      `${t('scheduling')}: ${t('phase')} ${label}`;
  return (
    <Tooltip title={description}>
      <Typography
        component="span"
        color="primary"
        className={className}
        aria-label={description}
        style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0, whiteSpace: 'nowrap' }}
      >
        <span aria-hidden="true" style={{ whiteSpace: 'pre' }}>{' · '}</span>
        <EventNoteIcon style={{ fontSize: '1.2em', flexShrink: 0, marginRight: '0.3em' }} />
        {busy ? <CircularProgress color="inherit" size="1em" aria-label={description} /> : <>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
          {(failure || runtimeProblem) && <ErrorOutlineIcon color="error" style={{ fontSize: '1.2em' }} />}
        </>}
      </Typography>
    </Tooltip>
  );
}
